import React, {useEffect, useState} from "react";
import {useSiren} from "../SirenContext";
import {platformAPI} from "../../../api/axiosConfig";
import moment from "moment";
import * as XLSX from "xlsx";

type ImportCounts = {
    total: number;
    created: number;
    updated: number;
    archived: number;
    skipped: number;
    failed: number;
};

type ImportError = {
    row: number;
    field: string;
    message: string;
};

type ContactImport = {
    id: string;
    name: string;
    status: string;
    counts: ImportCounts;
    errors: ImportError[];
    createdAt: string;
};

function StatusBadge({status}: {status: string}) {
    const styles: Record<string, string> = {
        complete:   "bg-desaturated-green text-dark-green",
        error:      "bg-light-red text-red",
        processing: "bg-light-blue text-dark-blue",
        queued:     "bg-desaturated-grey text-grey",
    };
    const cls = styles[status] ?? "bg-desaturated-grey text-grey";
    return (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${cls}`}>
            {status}
        </span>
    );
}

function downloadSummary(imp: ContactImport) {
    const wb = XLSX.utils.book_new();

    const summaryRows = [
        ["Import Name", imp.name],
        ["Date", moment(imp.createdAt).format("DD MMM YYYY HH:mm")],
        ["Status", imp.status],
        [],
        ["Total", "Created", "Updated", "Archived", "Skipped", "Failed"],
        [imp.counts.total, imp.counts.created, imp.counts.updated, imp.counts.archived, imp.counts.skipped, imp.counts.failed],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "Summary");

    if (imp.errors && imp.errors.length > 0) {
        const errorRows = [
            ["Row", "Field", "Message"],
            ...imp.errors.map((e) => [e.row, e.field, e.message]),
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(errorRows), "Errors");
    }

    XLSX.writeFile(wb, `import-summary-${imp.name}-${moment(imp.createdAt).format("YYYY-MM-DD")}.xlsx`);
}

function CountStat({label, value, highlight}: {label: string; value: number; highlight?: boolean}) {
    return (
        <div className="flex flex-col items-center gap-0.5">
            <span className={`text-sm font-semibold ${highlight && value > 0 ? "text-red" : ""}`}>{value}</span>
            <span className="text-xs text-grey">{label}</span>
        </div>
    );
}

const PAGE_SIZE = 10;

export function ImportHistory() {
    const {clientID} = useSiren();
    const [imports, setImports] = useState<ContactImport[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    useEffect(() => {
        if (!clientID) return;
        setLoading(true);
        platformAPI
            .get<{results: ContactImport[]; hasMore: boolean}>("api/contacts/import", {
                params: {clientID, page, limit: PAGE_SIZE},
            })
            .then((res) => {
                setImports(res.data.results ?? []);
                setHasMore(res.data.hasMore ?? false);
            })
            .catch(() => setImports([]))
            .finally(() => setLoading(false));
    }, [clientID, page]);

    return (
        <div className="bg-white shadow-md rounded-md p-6 flex flex-col gap-4">
            <h1 className="text-lg font-semibold">Import History</h1>
            {loading && <p className="text-sm text-grey">Loading…</p>}
            {!loading && imports.length === 0 && (
                <p className="text-center text-sm text-grey py-8">No previous imports</p>
            )}
            {imports.map((imp) => (
                <div key={imp.id} className="border border-light-grey rounded-md p-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                            <div className="font-semibold text-sm">{imp.name}</div>
                            <div className="text-xs text-grey mt-0.5">{moment(imp.createdAt).format("DD MMM YYYY HH:mm")}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {imp.counts.failed > 0 && (
                                <button
                                    onClick={() => downloadSummary(imp)}
                                    className="text-xs text-blue underline"
                                >
                                    Download summary
                                </button>
                            )}
                            <StatusBadge status={imp.status} />
                        </div>
                    </div>
                    <div className="grid grid-cols-5 gap-2 text-center border-t border-light-grey pt-3">
                        <CountStat label="Created"  value={imp.counts.created} />
                        <CountStat label="Updated"  value={imp.counts.updated} />
                        <CountStat label="Archived" value={imp.counts.archived} />
                        <CountStat label="Skipped"  value={imp.counts.skipped} />
                        <CountStat label="Failed"   value={imp.counts.failed} highlight />
                    </div>
                </div>
            ))}
            {(page > 1 || hasMore) && (
                <div className="flex items-center justify-center gap-2 py-2">
                    <button
                        onClick={() => setPage((p) => p - 1)}
                        disabled={page === 1}
                        className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Prev
                    </button>
                    <button
                        onClick={() => setPage((p) => p + 1)}
                        disabled={!hasMore}
                        className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}
